import config from "../../config";

const googleApiKey = config.googleDriveClientId;
const googleAppId = config.googleDriveAppId;
const GOOGLE_API_SCRIPT_URL = "https://apis.google.com/js/api.js";
const GOOGLE_CLIENT_SCRIPT_URL = "https://apis.google.com/js/client.js";
const GOOGLE_API_READY_TIMEOUT_MS = 10_000;

type GoogleDrivePickerDoc = {
  id: string;
  name: string;
  type: string;
};

type GoogleAuthResult = {
  access_token?: string;
  error?: string;
  error_subtype?: string;
};

type GoogleDriveDocsView = {
  setParent(parent: string): GoogleDriveDocsView;
  setMimeTypes(mimeTypes: string): GoogleDriveDocsView;
  setIncludeFolders(includeFolders: boolean): GoogleDriveDocsView;
};

type GoogleDrivePickerBuilder = {
  enableFeature(feature: string): GoogleDrivePickerBuilder;
  setAppId(appId: string): GoogleDrivePickerBuilder;
  setOAuthToken(token: string): GoogleDrivePickerBuilder;
  addView(view: unknown): GoogleDrivePickerBuilder;
  setCallback(
    callback: (payload: { docs?: GoogleDrivePickerDoc[] }) => void
  ): GoogleDrivePickerBuilder;
  build(): {
    setVisible(isVisible: boolean): void;
  };
};

type GoogleApiWindow = Window & {
  gapi?: {
    load(api: string, callback?: () => void): void;
    client: {
      load(api: string, version: string): void;
    };
    auth: {
      authorize(
        options: {
          client_id: string;
          plugin_name: string;
          scope: string[];
          immediate: boolean;
        },
        callback: (authResult: { access_token?: string }) => void
      ): void;
    };
  };
  google?: {
    picker: {
      DocsView: new (viewId: string) => {
        setParent(parent: string): GoogleDriveDocsView;
        setMimeTypes(mimeTypes: string): GoogleDriveDocsView;
        setIncludeFolders(includeFolders: boolean): GoogleDriveDocsView;
      };
      DocsUploadView: new () => unknown;
      PickerBuilder: new () => GoogleDrivePickerBuilder;
      ViewId: {
        DOCS: string;
      };
      Feature: {
        MULTISELECT_ENABLED: string;
      };
    };
  };
};

function getGoogleWindow(): GoogleApiWindow {
  return window as GoogleApiWindow;
}

function isGoogleApiReady() {
  const googleWindow = getGoogleWindow();

  return Boolean(
    googleWindow.gapi?.load &&
    googleWindow.gapi.client &&
    googleWindow.gapi.auth?.authorize
  );
}

function loadScript(src: string) {
  return new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`
    );

    if (existingScript) {
      resolve();
      return;
    }

    const onLoad = () => {
      targetScript.dataset.loaded = "true";
      resolve();
    };
    const onError = () => {
      reject(new Error(`Failed to load Google Drive script: ${src}`));
    };
    const targetScript = existingScript ?? document.createElement("script");

    targetScript.addEventListener("load", onLoad, { once: true });
    targetScript.addEventListener("error", onError, { once: true });

    if (!existingScript) {
      targetScript.src = src;
      targetScript.async = true;
      targetScript.defer = true;
      document.head.append(targetScript);
    }
  });
}

function waitForGoogleApiReady(timeoutMs = GOOGLE_API_READY_TIMEOUT_MS) {
  return new Promise<void>((resolve, reject) => {
    if (isGoogleApiReady()) {
      resolve();
      return;
    }

    const deadline = Date.now() + timeoutMs;
    const checkReady = () => {
      if (isGoogleApiReady()) {
        resolve();
        return;
      }

      if (Date.now() >= deadline) {
        reject(new Error("Google Drive picker is unavailable"));
        return;
      }

      window.setTimeout(checkReady, 50);
    };

    checkReady();
  });
}

async function ensureGoogleApiReady() {
  if (isGoogleApiReady()) {
    return;
  }

  await Promise.all([
    loadScript(GOOGLE_API_SCRIPT_URL),
    loadScript(GOOGLE_CLIENT_SCRIPT_URL),
  ]);
  await waitForGoogleApiReady();
}

const fetchDriveDoc = (link: string, oauthToken: string): Promise<Blob> => {
  return new Promise<Blob>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", link);
    xhr.responseType = "arraybuffer";
    xhr.setRequestHeader("Authorization", `Bearer ${oauthToken}`);
    xhr.addEventListener("load", (event) => {
      const target = event.currentTarget as XMLHttpRequest | null;
      const blob = new Blob([target?.response]);
      resolve(blob);
    });
    xhr.addEventListener("error", (event) => {
      reject(event);
    });
    xhr.send();
  });
};

type GDriveFile = {
  name: string;
  type: "video" | "image";
  getImage(): Promise<Blob>;
};

function loadGoogleApiModule(api: string) {
  return new Promise<void>((resolve, reject) => {
    const googleWindow = getGoogleWindow();

    if (!googleWindow.gapi?.load) {
      reject(new Error("Google Drive API is unavailable"));
      return;
    }

    try {
      googleWindow.gapi.load(api, () => resolve());
    } catch (error) {
      reject(error);
    }
  });
}

function getGoogleAuthorizationError(authResult: GoogleAuthResult) {
  if (authResult.error_subtype) {
    return `Google Drive authorization failed: ${authResult.error} (${authResult.error_subtype})`;
  }

  if (authResult.error) {
    return `Google Drive authorization failed: ${authResult.error}`;
  }

  return "Google Drive authorization failed";
}

export async function selectFileFromGoogleDrive(): Promise<GDriveFile[]> {
  await ensureGoogleApiReady();
  await Promise.all([
    loadGoogleApiModule("auth"),
    loadGoogleApiModule("picker"),
  ]);

  return new Promise((resolve, reject) => {
    const googleWindow = getGoogleWindow();
    googleWindow.gapi?.client.load("drive", "v2");
    googleWindow.gapi?.auth.authorize(
      {
        client_id: googleApiKey,
        plugin_name: "sked-social-webapp",
        scope: ["https://www.googleapis.com/auth/drive.file"],
        immediate: false,
      },
      (authResult: GoogleAuthResult) => {
        if (!authResult.access_token) {
          reject(new Error(getGoogleAuthorizationError(authResult)));
          return;
        }
        const google = googleWindow.google;

        if (!google?.picker) {
          reject(new Error("Google Drive picker is unavailable"));
          return;
        }

        const oauthToken = authResult.access_token;
        const viewFiles = new google.picker.DocsView(google.picker.ViewId.DOCS)
          .setParent("root")
          .setMimeTypes(
            "image/heic,image/png,image/jpeg,image/jpg,image/heif,video/mp4,application/pdf"
          )
          .setIncludeFolders(true);
        const picker = new google.picker.PickerBuilder()
          .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
          .setAppId(googleAppId)
          .setOAuthToken(oauthToken)
          .addView(viewFiles)
          .addView(new google.picker.DocsUploadView())
          .setCallback(async ({ docs }: { docs?: GoogleDrivePickerDoc[] }) => {
            if (!docs) {
              return;
            }
            const downloads: GDriveFile[] = docs.map((doc) => ({
              getImage: () =>
                fetchDriveDoc(
                  `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
                  oauthToken
                ),
              name: doc.name,
              type: doc.type === "video" ? "video" : "image",
            }));

            resolve(downloads);
          })
          .build();
        picker.setVisible(true);
      }
    );
  });
}
